import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const article = getArticleBySlug(slug || "");
  const related = getRelatedArticles(slug || "");

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">{t("pageNotFound")}</h1>
          <p className="text-foreground-600 mb-6">{t("pageNotFoundDesc")}</p>
          <Link to="/" className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold">
            {t("backToHome")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Parse markdown content to JSX
  const renderContent = (content: string) => {
    const lines = content.split("\n");
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let listType: "ol" | "ul" = "ul";

    const flushList = () => {
      if (currentList.length === 0) return;
      const Tag = listType === "ol" ? "ol" : "ul";
      elements.push(
        <Tag key={elements.length} className={`my-4 space-y-2 text-sm text-foreground-700 leading-relaxed ${Tag === "ol" ? "list-decimal pl-5" : "list-disc pl-5"}`}>
          {currentList.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </Tag>
      );
      currentList = [];
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (trimmed.startsWith("## ")) {
        flushList();
        elements.push(
          <h2 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
            {trimmed.replace("## ", "")}
          </h2>
        );
      } else if (trimmed.startsWith("### ")) {
        flushList();
        elements.push(
          <h3 key={idx} className="font-heading text-lg font-bold text-foreground-900 mt-8 mb-3">
            {trimmed.replace("### ", "")}
          </h3>
        );
      } else if (trimmed.startsWith("| ")) {
        flushList();
        // Simple table rendering - skip separator lines
        if (!trimmed.includes("---")) {
          const cells = trimmed.split("|").filter(c => c.trim());
          if (cells.length > 0) {
            elements.push(
              <div key={idx} className="overflow-x-auto my-6">
                <div className="flex gap-4 min-w-full">
                  {cells.map((cell, ci) => (
                    <div key={ci} className={`flex-1 px-4 py-3 rounded-lg text-sm ${ci === 0 ? 'bg-primary-100 text-primary-800 font-semibold' : 'bg-background-100 text-foreground-700'}`}>
                      {cell.trim()}
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        }
      } else if (trimmed.match(/^\d+\./)) {
        if (listType !== "ol") flushList();
        listType = "ol";
        currentList.push(trimmed.replace(/^\d+\.\s*/, ""));
      } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        if (listType !== "ul") flushList();
        listType = "ul";
        currentList.push(trimmed.replace(/^[-*]\s*/, ""));
      } else if (trimmed === "") {
        flushList();
      } else {
        flushList();
        elements.push(
          <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
            {trimmed}
          </p>
        );
      }
    });

    flushList();
    return elements;
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumbs */}
      <div className="pt-24 md:pt-28 pb-4 bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-foreground-900 transition-colors">{t("breadcrumbHome")}</Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <Link to="/guides" className="hover:text-foreground-900 transition-colors">{t("navGuides")}</Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <span className="text-foreground-900">{t(article.titleKey)}</span>
            </nav>
          </div>
        </div>
      </div>

      <article className="py-10 md:py-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <div className="mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold uppercase tracking-wider mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                {t(article.titleKey)}
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                {t(article.descKey)}
              </p>
              <div className="mt-6 flex items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {t("lastUpdated")}: {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" />
                  Editorial Team
                </span>
              </div>
            </div>

            {/* Back to Home Link */}
            <div className="mb-8 p-4 rounded-xl bg-primary-50/70 border border-primary-200/50">
              <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors">
                <i className="ri-arrow-left-line" />
                {t("backToHome")}
              </Link>
            </div>

            {/* Article Content */}
            <div className="prose prose-sm max-w-none">
              {renderContent(article.content)}
            </div>

            {/* CTA */}
            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-primary-500/10 border border-primary-200/50 text-center">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">
                {t("finalCtaTitle")}
              </h3>
              <p className="text-foreground-600 mb-6 text-sm">
                {t("finalCtaDesc")}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-block px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors"
              >
                {t("heroCtaPrimary")}
              </a>
            </div>

            {/* Related Articles */}
            {related.length > 0 && (
              <div className="mt-12">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  {t("relatedArticles")}
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {related.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all"
                    >
                      <span className="inline-block px-2 py-0.5 rounded-md bg-background-200/70 text-foreground-500 text-xs font-medium uppercase tracking-wider mb-2">
                        {rel.category}
                      </span>
                      <h4 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2">
                        {t(rel.titleKey)}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* All Articles Link */}
            <div className="mt-8 p-4 rounded-xl bg-background-100 border border-background-200/70">
              <Link to="/guides" className="flex items-center justify-between text-sm font-semibold text-foreground-700 hover:text-primary-700 transition-colors">
                <span>{t("sectionArticles")}</span>
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}